import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

interface OrderSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subtotal: number;
  serviceTax: number;
  serviceTaxPercentage: number;
  entryAmount: number | null;
  entryTypeName?: string;
  total: number;
  itemCount: number;
}

export function OrderSummaryDialog({
  open,
  onOpenChange,
  subtotal,
  serviceTax,
  serviceTaxPercentage,
  entryAmount,
  entryTypeName,
  total,
  itemCount,
}: OrderSummaryDialogProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Resumo do Pedido</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Produtos */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Produtos</span>
              <span className="text-xs text-muted-foreground/70">
                ({itemCount} {itemCount === 1 ? "item" : "itens"})
              </span>
            </div>
            <span className="font-medium">{formatCurrency(subtotal)}</span>
          </div>

          {/* Taxa de Serviço */}
          {serviceTax > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-blue-600 dark:text-blue-400">
                Taxa de Serviço ({serviceTaxPercentage}%)
              </span>
              <span className="font-medium text-blue-600 dark:text-blue-400">
                {formatCurrency(serviceTax)}
              </span>
            </div>
          )}

          {/* Entrada */}
          {entryAmount !== null && entryAmount > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-orange-600 dark:text-orange-400">
                {entryTypeName || "Entrada"}
              </span>
              <span className="font-medium text-orange-600 dark:text-orange-400">
                {formatCurrency(entryAmount)}
              </span>
            </div>
          )}

          <Separator />

          {/* Total */}
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-2xl font-bold text-primary">
              {formatCurrency(total)}
            </span>
          </div>

          {/* Detalhamento da composição */}
          <div className="bg-muted/50 rounded-lg p-3 mt-4">
            <p className="text-xs text-muted-foreground mb-2">Composição do valor:</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span>Subtotal dos produtos</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {serviceTax > 0 && (
                <div className="flex justify-between text-blue-600 dark:text-blue-400">
                  <span>+ Taxa de serviço</span>
                  <span>{formatCurrency(serviceTax)}</span>
                </div>
              )}
              {entryAmount !== null && entryAmount > 0 && (
                <div className="flex justify-between text-orange-600 dark:text-orange-400">
                  <span>+ {entryTypeName || "Entrada"}</span>
                  <span>{formatCurrency(entryAmount)}</span>
                </div>
              )}
              <Separator className="my-1" />
              <div className="flex justify-between font-semibold">
                <span>= Total a pagar</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
